<template>
    <div>
        <v-data-table
            :headers="filteredHeaders"
            :items="workOrdersFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            fixed-header
            :height="$vuetify.breakpoint.mdAndDown ? '69vh' : '64vh'"
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Work Orders Per Page',
            }"
            multiple-expand
            :expanded.sync="expanded"
            show-expand
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">WORK ORDERS BALANCE</h1>
                        <v-spacer />
                        <v-btn color="primary" @click="exportToExcel">
                            <v-icon left>mdi-file-excel</v-icon>
                            Export to Excel
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template
                v-for="h in headers"
                v-slot:[`header.${h.value}`]="{ header }"
            >
                <div :key="h.value" v-if="hasFilter(header.value)">
                    <v-text-field
                        class="py-2 mt-2"
                        :label="header.text"
                        v-model="filters[header.value].value"
                        dense
                        @click.stop
                    />
                    <v-select
                        :items="filterOptions"
                        v-model="filters[header.value].operator"
                        label="Condition"
                        dense
                        class="mt-0"
                        @click.stop
                    ></v-select>
                </div>
                <div :key="h.text" v-else>
                    {{ header.text }}
                </div>
            </template>

            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                    @click.stop
                />
                <v-select
                    :items="codeSortOptions"
                    v-model="selectedCodeSortOption"
                    label="Sort Option"
                    dense
                    class="mt-0"
                    @click.stop
                ></v-select>
            </template>

            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.projectManager`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectManagerToFilter"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.projectManager`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.projectManager.name }}</p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.project.reference }} - {{ item.project.name }}
                    </p>
                </div>
            </template>

            <template v-slot:[`item.progress`]="{ item }">
                <div class="d-flex justify-center">
                    <v-progress-linear
                        v-model="item.progress"
                        height="25"
                        :color="getProgressColor('progress', item.progress)"
                        rounded
                        class="no-interaction"
                    >
                        <strong>{{ item.progress.toFixed(1) }}%</strong>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.packingProgress`]="{ item }">
                <div class="d-flex justify-center">
                    <v-progress-linear
                        :value="item.packingProgress"
                        height="25"
                        :color="
                            getProgressColor('progress', item.packingProgress)
                        "
                        rounded
                        class="no-interaction"
                    >
                        <strong>{{ item.packingProgress.toFixed(1) }}%</strong>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.woQty`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.woQty }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.weight`]="{ item }">
                <div class="d-flex justify-end">
                    <p class="my-0">{{ (item.weight || 0).toFixed(1) }} kg</p>
                </div>
            </template>
            <template v-slot:[`item.packableWeight`]="{ item }">
                <div class="d-flex justify-end">
                    <p class="my-0">
                        {{ (item.packableWeight || 0).toFixed(1) }} kg
                    </p>
                </div>
            </template>
            <template v-slot:[`item.producedMass`]="{ item }">
                <div class="d-flex justify-end">
                    <p class="my-0">
                        {{ (item.producedMass || 0).toFixed(1) }} kg
                    </p>
                </div>
            </template>
            <template v-slot:[`item.packedMass`]="{ item }">
                <div class="d-flex justify-end">
                    <p class="my-0">
                        {{ (item.packedMass || 0).toFixed(1) }} kg
                    </p>
                </div>
            </template>

            <template
                v-slot:expanded-item="{
                    headers,
                    item: item,
                }"
            >
                <td
                    :colspan="headers.length"
                    class="pa-4 ma-0 outlined"
                    :style="{ background: 'white' }"
                >
                    <v-row no-gutters>
                        <v-col cols="12">
                            <v-data-table
                                :headers="subheaders"
                                :items="item.items"
                                class="elevation-0"
                                :mobile-breakpoint="0"
                                :loading="loading"
                                disable-pagination
                                hide-default-footer
                            >
                                <!--ITEMS-->
                                <template v-slot:[`item.woProgress`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <v-progress-linear
                                            :value="item.woProgress || 0"
                                            height="20"
                                            :color="
                                                getProgressColor(
                                                    'progress',
                                                    item.woProgress || 0
                                                )
                                            "
                                            rounded
                                            class="no-interaction"
                                        >
                                            <strong
                                                >{{
                                                    (
                                                        item.woProgress || 0
                                                    ).toFixed(1)
                                                }}%</strong
                                            >
                                        </v-progress-linear>
                                    </div>
                                </template>

                                <template
                                    v-slot:[`item.packingProgress`]="{ item }"
                                >
                                    <div class="d-flex justify-center">
                                        <v-progress-linear
                                            :value="item.packingProgress || 0"
                                            height="20"
                                            :color="
                                                getProgressColor(
                                                    'progress',
                                                    item.packingProgress || 0
                                                )
                                            "
                                            rounded
                                            class="no-interaction"
                                        >
                                            <strong>{{
                                                item.processes &&
                                                item.processes.includes(
                                                    packingProcess.id
                                                )
                                                    ? `${(
                                                          item.packingProgress ||
                                                          0
                                                      ).toFixed(1)} %`
                                                    : 'N/A'
                                            }}</strong>
                                        </v-progress-linear>
                                    </div>
                                </template>
                                <template v-slot:[`item.qty`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.quantity }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.suggestedQty`]="{ item }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.suggestedQty || 0 }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.releasedQty`]="{ item }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.released }}
                                        </p>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.receivedQty`]="{ item }"
                                >
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.received }}
                                        </p>
                                    </div>
                                </template>
                                <template v-slot:[`item.shippedQty`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.shipped }}
                                        </p>
                                    </div>
                                </template>
                                <template v-slot:[`item.balanceQty`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.balance }}
                                        </p>
                                    </div>
                                </template>
                                <template v-slot:[`item.pendingQty`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{
                                                (item.released || 0) -
                                                    (item.received || 0)
                                            }}
                                        </p>
                                    </div>
                                </template>
                                <template v-slot:[`item.packedQty`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.packedQty || 0 }}
                                        </p>
                                    </div>
                                </template>
                                <template v-slot:[`item.actions`]="{ item }">
                                    <div>
                                        <v-menu rounded offset-y>
                                            <template
                                                v-slot:activator="{ attrs, on }"
                                            >
                                                <v-btn
                                                    icon
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    small
                                                    depressed
                                                >
                                                    <v-icon
                                                        >mdi-dots-vertical</v-icon
                                                    >
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <v-list-item
                                                    @click="
                                                        openDialogPackingBalance(
                                                            item,
                                                            'delivery'
                                                        )
                                                    "
                                                >
                                                    <v-list-item-icon>
                                                        <v-icon
                                                            >mdi-send</v-icon
                                                        >
                                                    </v-list-item-icon>
                                                    <v-list-item-title>
                                                        Delivery log
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <v-list-item
                                                    @click="
                                                        openDialogPackingBalance(
                                                            item,
                                                            'quality'
                                                        )
                                                    "
                                                >
                                                    <v-list-item-icon>
                                                        <v-icon
                                                            >mdi-check</v-icon
                                                        >
                                                    </v-list-item-icon>
                                                    <v-list-item-title>
                                                        Quality log
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </td>
            </template>
        </v-data-table>
        <v-dialog
            :retain-focus="false"
            v-model="itemLogDialog"
            persistent
            max-width="1200px"
        >
            <ItemLog
                v-if="itemLogDialog"
                :viewType="viewLogType"
                :users="users"
                :selectedItem="selectedItem"
                :workOrder="{ status: 'balance' }"
                @closeDialog="itemLogDialog = false"
            />
        </v-dialog>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { firestore } from '@/services/firebase'
import API from '@/services/api'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import ItemLog from '../WorkOrders/ItemLog.vue'
import * as XLSX from 'xlsx/xlsx.mjs'
import WOMethods from '@/components/WorkOrders/WOMethods.js'
export default {
    name: 'PackingBalance',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
        project: {
            type: Object,
            deafult: () => ({}),
        },
    },
    components: {
        ItemLog,
    },
    data: () => ({
        workOrder: false,
        projectToFilter: undefined,
        projectToFilterDebounced: undefined,
        projectManagerToFilter: undefined,
        projectManagerToFilterDebounced: undefined,
        loading: false,
        workOrders: [],
        search: null,
        searchDebounced: null,
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
                width: 155,
            },
            {
                text: 'PROJECT MANAGER',
                value: 'projectManager',
                align: 'center',
                sortable: false,
                width: 240,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
                width: 240,
            },
            {
                text: 'WO PROGRESS',
                value: 'progress',
                align: 'center',
                sortable: true,
                width: 120,
            },
            {
                text: 'PACKING PROGRESS',
                value: 'packingProgress',
                align: 'center',
                sortable: true,
                width: 120,
            },
            {
                text: 'WO QTY',
                value: 'woQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'TOTAL MASS',
                value: 'weight',
                align: 'right',
                sortable: true,
                width: 115,
            },
            {
                text: 'PACKABLE MASS',
                value: 'packableWeight',
                align: 'right',
                sortable: true,
                width: 115,
            },
            {
                text: 'PRODUCED QTY',
                value: 'producedQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'PRODUCED MASS',
                value: 'producedMass',
                align: 'right',
                sortable: true,
                width: 115,
            },
            {
                text: 'SUGGESTED QTY TOTAL',
                value: 'suggestedQtyTotal',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'RELEASED',
                value: 'releasedQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'RECEIVED',
                value: 'receivedQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'RECEIVED PENDING TO RELEASE',
                value: 'qtyReceivedPendingToRelease',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'PENDING TO RECEIVE',
                value: 'pendingtoReceive',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'PENDING',
                value: 'pendingQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'PACKED QTY',
                value: 'packedQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'PACKED MASS',
                value: 'packedMass',
                align: 'right',
                sortable: true,
                width: 115,
            },
            {
                text: 'SHIPPED',
                value: 'shippedQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
            {
                text: 'BALANCE',
                value: 'balanceQty',
                align: 'center',
                sortable: true,
                width: 170,
            },
        ],
        subheaders: [
            {
                text: 'PART NUMBER',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },

            {
                text: 'WO PROGRESS',
                value: 'woProgress',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PACKING PROGRESS',
                value: 'packingProgress',
                align: 'center',
                sortable: false,
            },
            {
                text: 'QTY',
                value: 'quantity',
                align: 'center',
                sortable: true,
            },
            {
                text: 'PRODUCED QTY',
                value: 'producedQty',
                align: 'center',
                sortable: true,
            },
            {
                text: 'SUGGESTED QTY',
                value: 'suggestedQty',
                align: 'center',
                sortable: true,
            },
            {
                text: 'RELEASED',
                value: 'releasedQty',
                align: 'center',
                sortable: true,
            },
            {
                text: 'RECEIVED',
                value: 'receivedQty',
                align: 'center',
                sortable: true,
            },
            {
                text: 'PENDING',
                value: 'pendingQty',
                align: 'center',
                sortable: true,
            },
            {
                text: 'PACKED QTY',
                value: 'packedQty',
                align: 'center',
                sortable: true,
            },

            {
                text: 'SHIPPED',
                value: 'shippedQty',
                align: 'center',
                sortable: true,
            },
            {
                text: 'BALANCE',
                value: 'balanceQty',
                align: 'center',
                sortable: true,
            },
            {
                text: 'ORIGIN PROCESS',
                value: 'originProcess',
                align: 'center',
                sortable: false,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
        company: JSON.parse(localStorage.getItem('company')),
        listener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        settingProcesses: undefined,
        readyForPackingProcess: {
            id: 'readyForPacking',
            name: 'Ready for packing',
        },
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        qualityProcess: {
            id: 'quality',
            name: 'quality',
        },
        user: {},
        packingLists: [],
        startDate: 0,
        queryStartDate: 0,
        structuredWorkOrders: [],
        expanded: [],
        users: [],
        itemLogDialog: false,
        selectedItem: {},
        viewLogType: '',
        //new filters
        filters: {
            woQty: { value: '', operator: 'contains' },
            suggestedQtyTotal: { value: '', operator: 'contains' },
            releasedQty: { value: '', operator: 'contains' },
            receivedQty: { value: '', operator: 'contains' },
            pendingQty: { value: '', operator: 'contains' },
            packedQty: { value: '', operator: 'contains' },
            shippedQty: { value: '', operator: 'contains' },
            balanceQty: { value: '', operator: 'contains' },
            pendingtoReceive: { value: '', operator: 'contains' },
            qtyReceivedPendingToRelease: { value: '', operator: 'contains' },
            producedQty: { value: '', operator: 'contains' },
            weight: { value: '', operator: 'contains' },
            packableWeight: { value: '', operator: 'contains' },
            producedMass: { value: '', operator: 'contains' },
            packedMass: { value: '', operator: 'contains' },
        },
        filterOptions: [
            { text: 'Contains', value: 'contains' },
            { text: 'Is equal to', value: 'equals' },
            { text: 'Is different to', value: 'notEquals' },
            { text: 'Greater than', value: 'greaterThan' },
            { text: 'Less than', value: 'lessThan' },
        ],
        codeSortOptions: [
            { text: 'Unordered', value: 'unordered' },
            { text: 'Ascending', value: 'ascending' },
            { text: 'Descending', value: 'descending' },
        ],
        selectedCodeSortOption: 'unordered',
        dynamicFilters: [],
        debouncedDynamicFilters: [],
        woMethods: new WOMethods(),
        deletedWorkOrdersIds: [],
    }),
    computed: {
        ...mapState(['notificationResource']),
        isFromWorkOrders() {
            return this.$route.name === 'wo-packing-balance'
        },
        filteredHeaders() {
            if (this.isFromWorkOrders) {
                // Hide these columns when accessed from Work Orders
                const hiddenColumns = [
                    'receivedQty',
                    'qtyReceivedPendingToRelease',
                    'pendingtoReceive',
                ]
                return this.headers.filter(
                    h => !hiddenColumns.includes(h.value)
                )
            }
            return this.headers
        },
        activeFilters() {
            if (this.isFromWorkOrders) {
                // Remove filters for hidden columns
                const hiddenColumns = [
                    'receivedQty',
                    'qtyReceivedPendingToRelease',
                    'pendingtoReceive',
                ]
                const activeFilters = { ...this.filters }
                hiddenColumns.forEach(col => {
                    delete activeFilters[col]
                })
                return activeFilters
            }
            return this.filters
        },
        workOrdersFiltered() {
            let conditions = []
            conditions.push(this.filterByPackableQty)
            if (this.searchDebounced) {
                conditions.push(this.filterOrder)
            }
            if (this.projectToFilterDebounced) {
                conditions.push(this.filterProject)
            }
            if (this.projectManagerToFilterDebounced) {
                conditions.push(this.filterProjectManager)
            }

            if (this.debouncedDynamicFilters.length > 0) {
                conditions.push(order =>
                    this.filterDynamic(order, this.debouncedDynamicFilters)
                )
            }
            const sortedWorkOrders = this.getSortedWorkOrders(
                this.structuredWorkOrders
            )
            if (conditions.length > 0) {
                return sortedWorkOrders.filter(order => {
                    return conditions.every(condition => {
                        return condition(order)
                    })
                })
            }
            return sortedWorkOrders
        },
    },
    created() {
        this.debouncedSearch = _.debounce(value => {
            this.searchDebounced = value
        }, 350)
        this.debouncedProjectFilter = _.debounce(value => {
            this.projectToFilterDebounced = value
        }, 350)
        this.debouncedProjectManagerFilter = _.debounce(value => {
            this.projectManagerToFilterDebounced = value
        }, 350)
        this.debouncedUpdateDynamicFilters = _.debounce(() => {
            this.debouncedDynamicFilters = this.buildDynamicFilters(
                this.activeFilters
            )
        }, 350)
        this.debouncedDynamicFilters = this.buildDynamicFilters(
            this.activeFilters
        )
    },
    watch: {
        search: function(newValue) {
            this.debouncedSearch(newValue)
        },
        projectToFilter: function(newValue) {
            this.debouncedProjectFilter(newValue)
        },
        projectManagerToFilter: function(newValue) {
            this.debouncedProjectManagerFilter(newValue)
        },
        filters: {
            handler() {
                this.debouncedUpdateDynamicFilters()
            },
            deep: true,
        },
        '$route.name': function() {
            this.debouncedUpdateDynamicFilters()
        },
        startDate: async function(newStartDate) {
            try {
                if (newStartDate > 0 && newStartDate != this.queryStartDate) {
                    this.queryStartDate = newStartDate
                    this.loading = true
                    this.packingLists = await API.getAllPackingLists({
                        startDate: moment
                            .unix(newStartDate)
                            .format('YYYY-MM-DD'),
                    })
                    this.structureData()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            const {
                data: { settings },
            } = await API.getSettings()
            this.settingProcesses = settings.find(s => s.name == 'Processes')
            this.projects = await API.getWoProjects()
            const {
                data: { users },
            } = await API.getWoUsers()
            this.users = users
            await this.getWOFromSDK()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.debouncedSearch) this.debouncedSearch.cancel()
        if (this.debouncedProjectFilter) this.debouncedProjectFilter.cancel()
        if (this.debouncedProjectManagerFilter) {
            this.debouncedProjectManagerFilter.cancel()
        }
        if (this.debouncedUpdateDynamicFilters) {
            this.debouncedUpdateDynamicFilters.cancel()
        }
        if (this.listener) {
            this.listener()
            this.listener = null
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        async loadAllActiveWorkOrders() {
            return await this.woMethods.loadAllActiveWorkOrders(
                this.companyId,
                ['approved', 'finished', 'deleted', 'rejected', 'closed']
            )
        },
        async getWOFromSDK() {
            try {
                this.loading = true

                let startDate = new Date()

                // Query with status filter only (readAllWO filtered in code)
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('workOrders')
                    .where('status', 'in', [
                        'approved',
                        'finished',
                        'deleted',
                        'rejected',
                        'closed',
                    ])
                    .where('updatedOn', '>=', startDate)

                // Load initial data from backup
                let initialData = await this.loadAllActiveWorkOrders()

                let data = []
                //filter by status
                initialData = initialData.filter(wo =>
                    ['approved', 'finished'].includes(wo.status)
                )
                for (let wo of initialData) {
                    wo = await this.formatWorkOrder(wo)
                    if (wo) {
                        data.push(wo)
                    }
                }

                this.workOrders = data
                this.listener = query.onSnapshot(async snapshot => {
                    const changes = Array.from(snapshot.docChanges())
                    const processedChanges = await Promise.all(
                        changes.map(async change => {
                            let wo = Object.assign(change.doc.data(), {
                                id: change.doc.id,
                            })
                            wo = await this.formatWorkOrder(wo)
                            return { change, wo }
                        })
                    )

                    // Process all changes after all have been formatted
                    processedChanges.forEach(({ change, wo }) => {
                        if (wo) {
                            if (
                                change.type === 'added' ||
                                change.type === 'modified'
                            ) {
                                const index = data.findIndex(w => w.id == wo.id)
                                if (index >= 0) {
                                    // Use $set to ensure Vue reactivity
                                    this.$set(data, index, wo)
                                } else {
                                    data.push(wo)
                                }
                            }
                            if (
                                change.type === 'removed' ||
                                !['approved', 'finished'].includes(wo.status)
                            ) {
                                const index = data.findIndex(w => w.id == wo.id)
                                if (index >= 0) {
                                    data.splice(index, 1)
                                }
                            }
                        }
                    })
                    // Force array update to ensure reactivity
                    this.workOrders = [...data]
                    this.getStartDate()
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async formatWorkOrder(wo) {
            try {
                const workOrder = wo

                // Filter by status (already in query, but double check)
                if (
                    ![
                        'approved',
                        'finished',
                        'deleted',
                        'rejected',
                        'closed',
                    ].includes(workOrder.status)
                ) {
                    return undefined
                }

                // Filter by readAllWO permission in code (not in query)
                if (
                    !this.user?.permissions?.includes('readAllWO') &&
                    workOrder.createdBy != this.user.id &&
                    workOrder.projectManager != this.user.id
                ) {
                    return undefined
                }

                // Associate projectManager
                const projectManagerUser = this.users[workOrder.projectManager]
                if (projectManagerUser) {
                    workOrder.projectManager = projectManagerUser
                } else {
                    workOrder.projectManager = {
                        id: workOrder.projectManager,
                        name: '',
                    }
                }

                // Associate project
                workOrder.project = this.projects[workOrder.projectId] || {}
                if (
                    !workOrder.project ||
                    Object.keys(workOrder.project).length == 0
                ) {
                    workOrder.project = await API.getProject(
                        workOrder.projectId
                    )
                    // Add project to projects object
                    this.projects[workOrder.projectId] = workOrder.project
                }

                // Format items
                const formattedWO = this.woMethods.formatItems(workOrder)
                return formattedWO || workOrder
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                return undefined
            }
        },
        exportToExcel() {
            this.exportWorkOrdersToExcel(this.structuredWorkOrders)
        },
        filterOrder(order) {
            return (order._codeKey || '').includes(
                (this.searchDebounced || '').toLowerCase()
            )
        },
        filterProject(order) {
            return (order._projectKey || '').includes(
                (this.projectToFilterDebounced || '').toLowerCase()
            )
        },
        filterProjectManager(order) {
            return (order._projectManagerKey || '').includes(
                (this.projectManagerToFilterDebounced || '').toLowerCase()
            )
        },
        filterDynamic(order, dynamicFilters = this.dynamicFilters) {
            let result = false
            dynamicFilters.forEach(filter => {
                if (filter.operator == 'equals') {
                    result =
                        order[filter.key] && +order[filter.key] == +filter.value
                } else if (filter.operator == 'greaterThan') {
                    result =
                        order[filter.key] && +order[filter.key] > +filter.value
                } else if (filter.operator == 'lessThan') {
                    result =
                        order[filter.key] && +order[filter.key] < +filter.value
                } else if (filter.operator == 'notEquals') {
                    result =
                        order[filter.key] && +order[filter.key] != +filter.value
                } else if (filter.operator == 'contains') {
                    result =
                        order[filter.key] &&
                        order[filter.key]
                            .toString()
                            .includes(filter.value.toString())
                }

                if (!result || !order[filter.key]) {
                    return false
                }
            })
            return result
        },
        buildDynamicFilters(activeFilters) {
            let dynamicFilters = []
            Object.keys(activeFilters).forEach(key => {
                const filter = activeFilters[key]
                if (
                    filter &&
                    filter.value != null &&
                    filter.value.toString().trim() != ''
                ) {
                    dynamicFilters.push({ key, ...filter })
                }
            })
            return dynamicFilters
        },
        filterByPackableQty(order) {
            return order.packableQty > 0
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('L')} ${moment
                .unix(seconds)
                .format('LT')}`
        },
        getStartDate() {
            let startDate = Date.now() / 1000
            this.workOrders.forEach(wo => {
                if (wo.items) {
                    wo.items.forEach(item => {
                        if (item.deliveries) {
                            item.deliveries.forEach(delivery => {
                                if (
                                    delivery.nextProcess &&
                                    (delivery.nextProcess.id ==
                                        this.readyForPackingProcess.id ||
                                        delivery.nextProcess.id ==
                                            this.packingProcess.id)
                                ) {
                                    const seconds =
                                        delivery.deliveredOn.seconds ||
                                        delivery.deliveredOn._seconds
                                    if (seconds < startDate) {
                                        startDate = seconds
                                    }
                                }
                            })
                        }
                    })
                }
            })
            this.startDate = startDate
        },
        formatPackingItems() {
            let packingItems = []
            this.packingLists.forEach(pl => {
                if (pl.packages) {
                    pl.packages.forEach(packageElement => {
                        if (packageElement.items) {
                            packageElement.items.forEach(item => {
                                packingItems.push({
                                    partNumber: item.partNumber,
                                    partNumberId: item.partNumberId,
                                    suggestedQty: [
                                        'inProgress',
                                        'rejected',
                                        'pending',
                                    ].includes(pl.status)
                                        ? +item.suggestedQty || 0
                                        : 0,
                                    packingQty: +item.packingQty || 0,
                                    status: pl.status,
                                    workOrder: item.workOrder,
                                })
                            })
                        }
                    })
                }
            })
            return packingItems
        },
        exportWorkOrdersToExcel(structuredWorkOrders) {
            const validWOs = structuredWorkOrders.filter(
                wo => Number(wo.balanceQty) !== 0
            )

            const rows = []

            for (const wo of validWOs) {
                const workOrderCode = wo.code ?? ''
                const project = wo.project
                    ? `${wo.project.reference ?? ''} - ${wo.project.name ?? ''}`
                    : ''
                const projectManager = wo.projectManager?.name ?? ''

                for (const item of wo.items ?? []) {
                    if (Number(item.balance) !== 0) {
                        const packedQty = item.packedQty || 0
                        const itemPackingProgress = item.quantity
                            ? ((packedQty / item.quantity) * 100).toFixed(2) +
                              '%'
                            : '0%'

                        rows.push({
                            workOrder: workOrderCode,
                            Project: project,
                            projectManager: projectManager,
                            partNumber: item.partNumber || '',
                            description: item.description || '',
                            scope: wo.notes || '',
                            ItemProgress: `${(item.progress || 0).toFixed(
                                2
                            )} %`,
                            ItemPackingProgress: itemPackingProgress,
                            Qty: item.quantity || 0,
                            TotalMass: `${(
                                (item.quantity || 0) * (item.mass || 0)
                            ).toFixed(1)} Kg`,
                            ProducedQty: item.producedQty || 0,
                            ProducedMass: `${(
                                (item.producedQty || 0) * (item.mass || 0)
                            ).toFixed(1)} Kg`,
                            Released: item.released || 0,
                            Received: item.received || 0,
                            Pending:
                                Number(item.released || 0) -
                                Number(item.received || 0),
                            packedQty: packedQty,
                            PackedMass: `${(
                                (item.packedQty || 0) * (item.mass || 0)
                            ).toFixed(1)} Kg`,
                            SuggestedQty: item.suggestedQty || 0,
                            ShippedQty: item.shipped || 0,
                            Balance: item.balance || 0,
                            OriginProcess: item.originProcess || '',
                        })
                    }
                }
            }

            if (rows.length === 0) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'There is no data to export.',
                })
                return
            }

            const columns = [
                'workOrder',
                'Project',
                'projectManager',
                'partNumber',
                'description',
                'scope',
                'ItemProgress',
                'ItemPackingProgress',
                'Qty',
                'TotalMass',
                'ProducedQty',
                'ProducedMass',
                'Released',
                'Received',
                'Pending',
                'packedQty',
                'PackedMass',
                'SuggestedQty',
                'ShippedQty',
                'Balance',
                'OriginProcess',
            ]

            const ws = XLSX.utils.json_to_sheet(rows, { header: columns })

            ws['!autofilter'] = { ref: `A1:O1` }

            ws['!cols'] = columns.map(col => ({
                wch: Math.max(col.length + 2, 15),
            }))

            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'WorkOrdersBalance')
            XLSX.writeFile(wb, 'work_orders_balance.xlsx')
        },

        structureData() {
            const packingItems = this.formatPackingItems()
            let structuredWorkOrders = _.cloneDeep(this.workOrders)
            structuredWorkOrders.forEach(workOrder => {
                let totalPackableWeight = 0
                let totalPackedWeight = 0
                let totalProducedWeight = 0
                let totalDelivered = 0
                let totalReceived = 0
                workOrder.items = workOrder.items || []
                workOrder.items.forEach(item => {
                    const packingRegisters = packingItems.filter(
                        packingItem =>
                            packingItem.workOrder == workOrder.code &&
                            packingItem.partNumber == item.partNumber
                    )

                    item.suggestedQty = 0
                    item.packedQty = 0
                    packingRegisters.forEach(register => {
                        item.packedQty += register.packingQty
                        item.suggestedQty += register.suggestedQty
                    })

                    const itemEntries = packingRegisters.filter(register =>
                        ['shipped', 'completed'].includes(register.status)
                    )
                    item.shipped = itemEntries.reduce(
                        (accum, entry) => accum + Number(entry.packingQty || 0),
                        0
                    )
                    item.received = 0
                    item.released = 0
                    item.sent = 0
                    item.originProcess = ''

                    if (item.deliveries) {
                        const deliveries = item.deliveries.filter(
                            register =>
                                register.nextProcess.id ==
                                    this.packingProcess.id ||
                                register.nextProcess.id ==
                                    this.readyForPackingProcess.id
                        )

                        item.sent = deliveries.reduce(
                            (total, register) => total + register.qty,
                            0
                        )
                        totalDelivered += item.sent
                        const received = deliveries.filter(
                            register => register.receivedBy
                        )

                        item.received = received.reduce(
                            (total, register) => total + register.qty,
                            0
                        )
                        totalReceived += item.received
                    }

                    item.balance =
                        Number(
                            item.processes.includes(this.packingProcess.id)
                                ? item.quantity || 0
                                : 0
                        ) - Number(item.shipped || 0)

                    //________releasedQty______
                    if (item.processes) {
                        if (item.processes.includes(this.packingProcess.id)) {
                            totalPackableWeight +=
                                item.quantity * (item.mass || 0)
                        }
                        const qualityIndex = item.processes.findIndex(
                            p => p == this.qualityProcess.id
                        )
                        if (
                            item.quality &&
                            qualityIndex > -1 &&
                            item.processes.length - 2 == qualityIndex &&
                            (item.processes[qualityIndex + 1] ==
                                this.packingProcess.id ||
                                item.processes[qualityIndex + 1] ==
                                    this.readyForPackingProcess.id)
                        ) {
                            if (qualityIndex - 1 > -1) {
                                const process = this.settingProcesses.processes.find(
                                    pr =>
                                        pr.id ==
                                        item.processes[qualityIndex - 1]
                                )
                                if (process) {
                                    item.originProcess = process.name
                                }
                            }

                            item.released = item.quality.reduce(
                                (accum, q) => accum + Number(q.releaseQty || 0),
                                0
                            )
                        } else if (
                            workOrder.assemblyWorkOrder == undefined &&
                            item.quality
                        ) {
                            const process = this.settingProcesses.processes.find(
                                pr =>
                                    pr.id ==
                                    item.processes[item.processes.length - 1]
                            )
                            if (process) {
                                item.originProcess = process.name
                            }

                            item.released = item.quality.reduce(
                                (accum, q) => accum + Number(q.releaseQty || 0),
                                0
                            )
                        } else if (
                            qualityIndex == -1 &&
                            !item.quality &&
                            item.processes.includes(this.packingProcess.id)
                        ) {
                            const packingIndex = item.processes.findIndex(
                                p => p.id == this.packingProcess.id
                            )
                            if (packingIndex > 0 && item.production) {
                                const production = item.production.filter(
                                    register =>
                                        register.process.id ==
                                        item.processes[packingIndex - 1]
                                )

                                item.released = production.reduce(
                                    (total, register) => total + register.qty,
                                    0
                                )
                            }
                        }
                    }

                    // progress
                    item.progressData = {}
                    if (item.processes) {
                        item.processes.forEach(processId => {
                            const process =
                                processId == this.readyForPackingProcess.id
                                    ? this.packingProcess.id
                                    : processId
                            if (processId != this.qualityProcess.id) {
                                item.progressData[process] = 0
                            }
                        })
                    }
                    if (item.production) {
                        item.production.forEach(entry => {
                            if (
                                entry.process.id ==
                                this.readyForPackingProcess.id
                            ) {
                                entry.process.id == this.packingProcess.id
                            }

                            const entryProcess =
                                entry.process.id ==
                                this.readyForPackingProcess.id
                                    ? this.packingProcess.id
                                    : entry.process.id

                            if (entryProcess != this.packingProcess.id) {
                                item.progressData[entryProcess] =
                                    item.progressData[entryProcess] || 0
                                item.progressData[entryProcess] += Number(
                                    entry.qty
                                )
                            }
                        })
                    }
                    if (item.packedQty) {
                        item.progressData[this.packingProcess.id] = Number(
                            item.packedQty
                        )
                        totalPackedWeight += item.packedQty * (item.mass || 0)
                    }
                    item.progress = 0
                    //get last process id
                    let lastProcessIndex = item.processes.length - 1
                    if (
                        item.processes[lastProcessIndex] ==
                        this.packingProcess.id
                    ) {
                        lastProcessIndex--
                    }

                    if (
                        item.processes[lastProcessIndex] ==
                        this.qualityProcess.id
                    ) {
                        lastProcessIndex--
                    }

                    item.producedQty =
                        item.progressData[item.processes[lastProcessIndex]] || 0

                    totalProducedWeight += +item.producedQty * (item.mass || 0)

                    Object.values(item.progressData).forEach(qty => {
                        item.progress += Number(qty)
                    })
                    item.progress =
                        (item.progress /
                            (item.quantity *
                                Object.values(item.progressData).length)) *
                        100
                    item.woProgress = this.getItemWOProgress(item)
                    item.packingProgress = this.getItemPackingProgress(item)
                })

                workOrder.releasedQty = this.getTotalReleased(workOrder)
                workOrder.packingProgress = this.getPackingProgress(workOrder)
                workOrder.progress = this.getTotalPercentage(workOrder)
                workOrder.receivedQty = this.getTotalReceived(workOrder)
                workOrder.pendingQty = this.getTotalPending(workOrder)
                workOrder.shippedQty = this.getTotalShipped(workOrder)
                workOrder.packableQty = this.getTotalPackableQty(workOrder)
                workOrder.balanceQty = this.getTotalBalance(workOrder)
                workOrder.packedQty = this.getTotalPackedQty(workOrder)
                workOrder.suggestedQtyTotal = this.getTotalSuggestedQty(
                    workOrder
                )
                workOrder.producedQty = this.getTotalProducedQty(workOrder)
                workOrder.woQty = this.getTotalWOQty(workOrder)
                workOrder.deliveredQty = workOrder.items.reduce(
                    (acc, item) =>
                        acc +
                        (item.deliveries
                            ? item.deliveries.reduce(
                                  (sum, d) => sum + (d.qty || 0),
                                  0
                              )
                            : 0),
                    0
                )

                workOrder.qtyReceivedPendingToRelease = this.getTotalReceivedPendingToRelease(
                    workOrder
                )

                workOrder.pendingtoReceive = totalDelivered - totalReceived
                workOrder.packableWeight = totalPackableWeight
                workOrder.packedMass = totalPackedWeight
                workOrder.producedMass = totalProducedWeight
                workOrder._codeKey = (workOrder.code || '').toLowerCase()
                workOrder._projectKey = `${workOrder.project?.name ||
                    ''} ${workOrder.project?.reference || ''}`
                    .toLowerCase()
                    .trim()
                workOrder._projectManagerKey = (
                    workOrder.projectManager?.name || ''
                ).toLowerCase()
                const [codeRef = '', codeSeq = '0'] = (
                    workOrder.code || ''
                ).split('-')
                workOrder._codeRef = codeRef
                workOrder._codeSeq = parseInt(codeSeq, 10) || 0
            })
            this.structuredWorkOrders = structuredWorkOrders
        },
        getItemWOProgress(item) {
            let total = 0
            let produced = 0
            total += item.quantity * Object.keys(item.progressData || {}).length
            produced += Object.values(item.progressData || {}).reduce(
                (acc, qty) => acc + Number(qty),
                0
            )
            return total > 0 ? (produced / total) * 100 : 0
        },
        getItemPackingProgress(item) {
            if (
                item.processes &&
                item.processes.includes(this.packingProcess.id)
            ) {
                return item.quantity > 0
                    ? (item.packedQty / item.quantity) * 100
                    : 0
            } else {
                return 100
            }
        },
        getPackingProgress(wo) {
            if (!wo.items) return 0

            const packableItems = wo.items.filter(item =>
                item.processes.includes(this.packingProcess.id)
            )

            const packedQty = packableItems.reduce(
                (acc, item) => acc + (item.packedQty || 0),
                0
            )

            const totalQty = packableItems.reduce(
                (acc, item) => acc + (item.quantity || 0),
                0
            )

            return totalQty > 0 ? (packedQty / totalQty) * 100 : 0
        },
        getTotalReceivedPendingToRelease(wo) {
            //received - released
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) =>
                        accum +
                        (+item.received - item.released > 0
                            ? +item.received - item.released
                            : 0),
                    0
                )
            } else {
                return 0
            }
        },
        getTotalPercentage(wo) {
            if (wo.items) {
                let produced = 0
                let total = 0
                wo.items.forEach(item => {
                    Object.values(item.progressData).forEach(qty => {
                        produced += Number(qty)
                    })
                    total += Number(
                        item.quantity * Object.values(item.progressData).length
                    )
                })
                if (total > 0) {
                    return (produced / total) * 100
                } else {
                    return 0
                }
            } else {
                return 0
            }
        },
        getProgressColor(key, percentage) {
            if (key == 'count') return 'primary'
            if (percentage < 25) return 'error'
            if (percentage < 50) return 'orange'
            if (percentage < 75) return 'warning'
            if (percentage < 100) return 'info'
            return 'success'
        },
        getTotalReleased(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) => accum + item.released,
                    0
                )
            } else {
                return 0
            }
        },
        getTotalReceived(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) => accum + item.received,
                    0
                )
            } else {
                return 0
            }
        },
        getTotalShipped(wo) {
            if (wo.items) {
                return Number(
                    wo.items.reduce((accum, item) => accum + item.shipped, 0)
                )
            } else {
                return 0
            }
        },
        getTotalBalance(wo) {
            if (wo.items) {
                return wo.items.reduce((accum, item) => accum + item.balance, 0)
            } else {
                return 0
            }
        },
        getTotalWOQty(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) => accum + +item.quantity,
                    0
                )
            } else {
                return 0
            }
        },
        getTotalProducedQty(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) => accum + (+item.producedQty || 0),
                    0
                )
            } else {
                return 0
            }
        },
        getTotalSuggestedQty(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) => accum + (item.suggestedQty || 0),
                    0
                )
            } else {
                return 0
            }
        },
        getTotalPackableQty(wo) {
            if (wo.items) {
                return wo.items
                    .filter(i => i.processes.includes(this.packingProcess.id))
                    .reduce((accum, item) => accum + item.quantity, 0)
            } else {
                return 0
            }
        },
        getTotalPending(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) =>
                        accum + ((item.released || 0) - (item.received || 0)),
                    0
                )
            } else {
                return 0
            }
        },
        getTotalPackedQty(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) => accum + (item.packedQty || 0),
                    0
                )
            } else {
                return 0
            }
        },
        getSortedWorkOrders(workOrders) {
            const sorted = [...workOrders]
            if (this.selectedCodeSortOption == 'unordered') {
                return sorted.sort(
                    (a, b) => (b.pendingQty || 0) - (a.pendingQty || 0)
                )
            }
            if (this.selectedCodeSortOption == 'ascending') {
                return sorted.sort((a, b) => {
                    const referenceComp = (a._codeRef || '').localeCompare(
                        b._codeRef || ''
                    )
                    if (referenceComp !== 0) {
                        return referenceComp
                    }
                    return (a._codeSeq || 0) - (b._codeSeq || 0)
                })
            }
            if (this.selectedCodeSortOption == 'descending') {
                return sorted.sort((a, b) => {
                    const referenceComp = (b._codeRef || '').localeCompare(
                        a._codeRef || ''
                    )
                    if (referenceComp !== 0) {
                        return referenceComp
                    }
                    return (b._codeSeq || 0) - (a._codeSeq || 0)
                })
            }
            return sorted
        },
        openDialogPackingBalance(item, logType) {
            this.viewLogType = logType
            this.selectedItem = _.cloneDeep(item)
            this.itemLogDialog = true
        },
        setDynamicFilters(dynamicFilters) {
            this.dynamicFilters = dynamicFilters
        },
        hasFilter(headerValue) {
            return Object.prototype.hasOwnProperty.call(
                this.activeFilters,
                headerValue
            )
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 72vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}
</style>
